// Library Methods
import { store } from '../index';

// Actions
import { isLoggedInSuccess } from '../redux/common/auth/auth.actions';

// Helper Methods
import { getFromLocalStorage, setToLocalStorage } from './helperMethods';

// Browser storage field names
import { LOCAL_STORAGE_FIELD_NAMES } from './constants/browserStorageFieldNames';

// Toast Messages
import { ERROR_MESSAGES } from './constants/toastMessages';

// API Response Keys
import { COMMON_RESPONSE_HEADER_KEYS } from './API/apiResponseKeys';
import { toast } from 'react-toastify';
import axios from 'axios';

// Create an instance of axios
const mainAxiosInstance = axios.create();

const GetIEVersion = () => {
    const sAgent = window.navigator.userAgent;
    const Idx = sAgent.indexOf('MSIE');

    // If IE, return version number.
    if (Idx > 0) {
        return parseInt(
            sAgent.substring(Idx + 5, sAgent.indexOf('.', Idx)),
            10
        );
    } else if (Boolean(navigator.userAgent.match(/Trident\/7\./))) {
        return 11;
    }

    return 0;
    // It is not IE
};

const csrfIdentifier = getFromLocalStorage(
    LOCAL_STORAGE_FIELD_NAMES.CSRF_IDENTIFIER
);

// Set default headers on our axios instance
mainAxiosInstance.defaults.headers['Content-Type'] = 'application/json';
mainAxiosInstance.defaults.headers.common.csrfIdentifier = csrfIdentifier;

if (GetIEVersion() > 0) {
    mainAxiosInstance.defaults.headers['Cache-Control'] = 'no-cache';
    mainAxiosInstance.defaults.headers.Pragma = 'no-cache';
    mainAxiosInstance.defaults.headers.Expires =
        'Sat, 01 Jan 2000 00:00:00 GMT';
    mainAxiosInstance.defaults.headers['If-Modified-Since'] = '0';
}

/**
 * To create request headers and options for xhr request
 *
 * @param {Object} headerOptions - Holds custom header options passed. Default value is {}
 * @returns {Object} - Holds final request headers and options for xhr request
 */
const setCsrfInRequestHeaders = () => {
    const csrfIdentifier = getFromLocalStorage(
        LOCAL_STORAGE_FIELD_NAMES.CSRF_IDENTIFIER
    );
    mainAxiosInstance.defaults.headers.common.csrfIdentifier = csrfIdentifier;
};

const toasting = (() => {
    let executed = false;
    return () => {
        if (!executed) {
            executed = true;
            toast.info(ERROR_MESSAGES.SESSION_TIME_OUT);
        }
    };
})();

// Add a response interceptor
mainAxiosInstance.interceptors.response.use(
    function(response) {
        // Look for csrfIdentifier in response headers
        const csrfIdentifier =
            response.headers[COMMON_RESPONSE_HEADER_KEYS.CSRF_IDENTIFIER];

        // If csrfIdentifier found then set it in localStorage
        if (csrfIdentifier) {
            setToLocalStorage(
                LOCAL_STORAGE_FIELD_NAMES.CSRF_IDENTIFIER,
                csrfIdentifier
            );

            setCsrfInRequestHeaders();
        }

        return response;
    },
    function(error) {
        // If HTTP status = 401 then session is timed out
        // So update loggedin status and update csrf from response header
        if (error.response.status === 401) {
            // Look for csrfIdentifier in response headers
            const csrfIdentifier =
                error.response.headers[
                    COMMON_RESPONSE_HEADER_KEYS.CSRF_IDENTIFIER
                ];

            // If csrfIdentifier found then set it in localStorage
            if (csrfIdentifier) {
                setToLocalStorage(
                    LOCAL_STORAGE_FIELD_NAMES.CSRF_IDENTIFIER,
                    csrfIdentifier
                );

                setCsrfInRequestHeaders();
            }
            // Set loggedin status to false in localStorage
            setToLocalStorage(LOCAL_STORAGE_FIELD_NAMES.IS_LOGGED_IN, false);

            if (window.location.pathname !== '/login') {
                toasting();
            }

            // Set isLoggedIn false in auth reducer
            store.dispatch(isLoggedInSuccess({ isLoggedIn: false }));
        }
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        return Promise.reject(error);
    }
);

/**
 * To create request headers and options for xhr request
 *
 * @param {Object} headerOptions - Holds custom header options passed. Default value is {}
 * @returns {Object} - Holds final request headers and options for xhr request
 */
const createRequestOptions = (headerOptions = {}) => {
    const options = {
        headers: {
            ...headerOptions,
        },
        withCredentials: true,
    };
    return { options };
};

export const fetchaxiosData = async(apiUrl, request, options) => {
    const axiosdata = await  mainAxiosInstance.post(apiUrl, request, options);
    // console.log(apiUrl, axiosdata);
    return axiosdata
}

/**
 * To make xhr "POST" request
 *
 * @param {String} apiUrl - URL of API
 * @param {Object} data - Data that needs to be sent
 * @param {Object} headerOptions - Request headers that get merged with default headers
 * @returns Promise object that gets resolved / reject depending on the result of API call
 */
const POST = (apiUrl, data = null, headerOptions, notStringify) => {
    try {
        const { options } = createRequestOptions(headerOptions);
        const request = notStringify ? data : JSON.stringify(data);
        // console.log("options", options, request);
    
        return fetchaxiosData(apiUrl, request, options);
    } catch(e) {
        console.log("e", e);
    }
    return {};
};

const DELETE = (apiUrl, data = null, headerOptions, notStringify) => {
    const { options } = createRequestOptions(headerOptions);
    const request = notStringify ? data : JSON.stringify(data);
    return mainAxiosInstance.delete(apiUrl, request, options);
};

/**
 * To make xhr "GET" request
 *
 * @param {String} apiUrl - URL of API
 * @param {Object} headerOptions - Request headers that get merged with default headers
 * @returns Promise object that gets resolved / reject depending on the result of API call
 */
const GET = (apiUrl, headerOptions) => {
    const { options } = createRequestOptions(headerOptions);
    return mainAxiosInstance.get(apiUrl, options);
};

export { setCsrfInRequestHeaders, POST, GET, DELETE };
